import { PartnerGroup } from '@provision/entity-types';
import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { MAX_QUERY_LIMIT } from 'src/graphql/constants';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';

const GET_PARTNER_GROUP_QUERY = gql`
  query GetPartnerGroup($params: QueryParams) @entity(name: "PartnerGroup") {
    getPartnerGroup(queryParams: $params) {
      records {
        id
        name
        partnerGroupId
        description
      }
    }
  }
`;

interface GetPartnerGroupsQuery {
  getPartnerGroup: Records<PartnerGroup>;
}

export interface PartnerGroupOption {
  id: string;
  value: string;
  label: string;
}

export const usePartnerGroups = (
  params: {
    partnerId: number | null | undefined;
    filters?: ConditionInput[];
  },
  options?: QueryHookOptions<GetPartnerGroupsQuery>,
) => {
  const defaultQueryFilters = [
    {
      attribute: 'partner/partnerId',
      operator: '=',
      value: String(params.partnerId),
    },
    {
      attribute: 'description',
      operator: '!=',
      value: '',
    },
  ];
  const { data, ...rest } = useQuery<GetPartnerGroupsQuery, QueryVariables>(
    GET_PARTNER_GROUP_QUERY,
    {
      skip: isNullish(params.partnerId),
      variables: {
        params: {
          filters: params.filters?.length ? params.filters : defaultQueryFilters,
          limit: MAX_QUERY_LIMIT,
          sorting: { attribute: 'name' },
        },
      },
      ...options,
    },
  );

  const partnerGroups = data?.getPartnerGroup?.records;

  return {
    partnerGroups,
    ...rest,
  };
};

export const usePartnerGroupsOptions = (
  params: {
    partnerId: number | null;
    filters?: ConditionInput[];
  },
  options?: QueryHookOptions<GetPartnerGroupsQuery>,
) => {
  const { partnerGroups } = usePartnerGroups(params, options);
  return useMemo(() => {
    if (!partnerGroups) return [];
    return partnerGroups.map((partnerGroup): PartnerGroupOption => ({
      id: partnerGroup.partnerGroupId,
      value: partnerGroup.partnerGroupId,
      label: partnerGroup.name,
    }));
  }, [partnerGroups]);
};

export const useAssignablePartnerGroups = (
  options?: QueryHookOptions<GetPartnerGroupsQuery>,
) => {
  const { data, ...rest } = useQuery<GetPartnerGroupsQuery, QueryVariables>(
    GET_PARTNER_GROUP_QUERY,
    {
      variables: {
        params: {
          limit: MAX_QUERY_LIMIT,
          sorting: { attribute: 'name' },
        },
      },
      ...options,
    },
  );

  return {
    partnerGroups: data?.getPartnerGroup?.records ?? [],
    ...rest,
  };
};

export const useAssignablePartnerGroupsOptions = (
  options?: QueryHookOptions<GetPartnerGroupsQuery>,
) => {
  const { partnerGroups, ...rest } = useAssignablePartnerGroups(options);

  const mappedOptions = useMemo(
    () =>
      partnerGroups.map((partnerGroup): PartnerGroupOption => ({
        id: partnerGroup.partnerGroupId,
        value: partnerGroup.partnerGroupId,
        label: partnerGroup.name,
      })),
    [partnerGroups],
  );

  return {
    options: mappedOptions,
    ...rest,
  };
};
